import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
  type Unsubscribe,
} from "firebase/firestore";
import type { Project } from "@shared/types";
import { projectBasePath } from "@domain/project/paths";
import { db } from "../firebase";

/**
 * Per-project rejected AI observations at
 *   <projectBasePath>/rejectedObservations/{fingerprint}
 *
 * Each doc records enough metadata to display a "rejected list" UI
 * (optional) and — more importantly — to be passed to the reviewer
 * flow so the LLM avoids repeating the same concern.
 */
export interface RejectedObservationDoc {
  id: string;
  title: string;
  body: string;
  category: string;
  rejectedAt: number;
  rejectedBy: string;
  reason?: string;
}

export const rejectedObservationRepository = {
  watch(
    project: Project,
    onChange: (items: RejectedObservationDoc[]) => void
  ): Unsubscribe {
    const col = collection(db, projectBasePath(project), "rejectedObservations");
    return onSnapshot(
      col,
      (snap) => {
        const items = snap.docs.map((d) => d.data() as RejectedObservationDoc);
        onChange(items);
      },
      (err) => {
        console.error("[rejectedObservationRepository.watch]", err);
        onChange([]);
      }
    );
  },

  async add(project: Project, data: RejectedObservationDoc): Promise<void> {
    const ref = doc(db, projectBasePath(project), "rejectedObservations", data.id);
    await setDoc(ref, data, { merge: true });
  },

  async remove(project: Project, id: string): Promise<void> {
    const ref = doc(db, projectBasePath(project), "rejectedObservations", id);
    await deleteDoc(ref);
  },
};
